import React, { useState, useEffect, useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Alert, AlertDescription,
  Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../ui'
import { resolverJugadores, asignarPlan } from '../../../../lib/supabase'
import { planesCompatibles, scopeLabel } from '../../../../constants/planScope'
import { planListoParaAsignar } from '../../../../lib/services/pagosPlanHelpers'
import { desglosarPorPlan, totalDelPlan } from '../../../../lib/planCobertura'
import { formatMoney } from '../../../../utils/helpers'

const ETIQUETA_UNIDAD = { grupo: 'grupo(s)', familia: 'familia(s)', jugador: 'jugador(es)' }

// Ultimo paso antes de generar cargos: primero se resuelve a quien le toca (la
// unidad marcada por el admin no es el destinatario), y solo entonces se
// habilita el boton de confirmar.
//
// `limitarA` (opcional): subconjunto de jugadores ya decidido en la pantalla --
// hoy lo usa el aviso "N sin plan", que abre este dialogo con la unidad del
// grupo/familia (para que el selector siga ofreciendo planes de ese alcance)
// pero solo debe tocar a los faltantes. Sin acotar, resolverJugadores devuelve
// el grupo COMPLETO y reasignar a quien ya tenia otro plan le cancela sus
// cargos pendientes: se le cambiaria el plan a gente que el admin nunca marco.
//
// `planesDe` (jugadorId -> [planId, ...]) lo carga el orquestador y es lo que
// permite desglosar el preview en tres cubos en vez de dar un total suelto: sin
// el, el caso destructivo (a quien tiene OTRO plan se le reemplaza y se le
// cancelan cargos) no se podia nombrar antes de confirmar.
const AsignarPlanDialog = ({
  open, onOpenChange, unidad, ids = [], limitarA = null, temporadaId,
  planes, planId, planesDe = {}, onDone,
}) => {
  const [elegido, setElegido] = useState(planId || '')
  const [preview, setPreview] = useState(null)
  const [aplicando, setAplicando] = useState(false)
  const [error, setError] = useState(null)

  // Solo planes que de verdad se pueden aplicar: uno a medias hace elegir
  // jugadores para reventar al final, cuando asignarPlan valida el cuadre.
  const compatibles = planesCompatibles(planes, unidad).filter(planListoParaAsignar)
  const hayIncompletos = planesCompatibles(planes, unidad).length > compatibles.length

  useEffect(() => {
    if (open) { setElegido(planId || ''); setPreview(null); setError(null) }
  }, [open, planId])

  // Se resuelve ANTES de escribir: el admin ve a cuantos se les va a cobrar.
  useEffect(() => {
    if (!open || !elegido) { setPreview(null); setError(null); return }
    let vivo = true
    setPreview(null)
    setError(null)
    resolverJugadores({ unidad, ids, temporadaId })
      .then((jugadorIds) => {
        if (!vivo) return
        // La interseccion va aqui, no en confirmar(): asi el conteo que se ve
        // en pantalla y lo que se escribe son SIEMPRE la misma lista.
        const permitidos = limitarA ? new Set(limitarA) : null
        setPreview({
          jugadorIds: permitidos ? jugadorIds.filter((id) => permitidos.has(id)) : jugadorIds,
        })
      })
      .catch((e) => { if (vivo) setError(e.message || 'No se pudo calcular a quién aplica.') })
    return () => { vivo = false }
  }, [open, elegido, unidad, ids, limitarA, temporadaId])

  const plan = compatibles.find((p) => p.id === elegido)

  const desglose = useMemo(
    () => (preview ? desglosarPorPlan(preview.jugadorIds, elegido, planesDe) : null),
    [preview, elegido, planesDe],
  )

  const confirmar = async () => {
    setAplicando(true)
    setError(null)
    try {
      const { error: err, count } = await asignarPlan({ planId: elegido, jugadorIds: preview.jugadorIds })
      if (err) { setError(err.message || String(err)); return }
      onDone?.({ jugadores: preview.jugadorIds.length, cargos: count })
    } catch (e) {
      // asignarPlan ya atrapa sus propios errores y devuelve { error }, pero
      // si de todas formas rechaza (o algo mas revienta aqui), aplicando no
      // puede quedarse pegado en true -- eso trabaria el dialogo (ver
      // manejarOpenChange, que bloquea el cierre mientras aplicando).
      setError(e.message || 'No se pudo asignar el plan.')
    } finally {
      setAplicando(false)
    }
  }

  // No se permite cerrar (Escape / clic afuera) mientras la asignacion esta
  // en vuelo -- el boton Cancelar ya respeta esto, el Dialog tambien debe.
  const manejarOpenChange = (siguiente) => { if (!aplicando) onOpenChange(siguiente) }

  const etiquetaUnidad = ETIQUETA_UNIDAD[unidad] || 'seleccionado(s)'
  const porJugador = totalDelPlan(plan)
  const parcialidades = (plan?.pagos_planes_parcialidades || []).length
  const nuevos = desglose ? desglose.sinPlan.length + desglose.conOtro.length : 0

  return (
    <Dialog open={open} onOpenChange={manejarOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Asignar plan</DialogTitle></DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Plan</Label>
            <Select value={elegido} onValueChange={setElegido}>
              <SelectTrigger><SelectValue placeholder="Seleccionar plan" /></SelectTrigger>
              <SelectContent>
                {compatibles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre} - {scopeLabel(p.scope)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {compatibles.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                {hayIncompletos
                  ? 'Los planes de esta temporada están incompletos: les falta el calendario de pagos o no cuadra con su total. Termínalos en Planes de pago.'
                  : 'No hay planes que se asignen de esta forma. Crea uno con alcance "Libre" o el que corresponda.'}
              </p>
            )}
          </div>

          {plan && !desglose && <p className="text-sm text-muted-foreground">Calculando a quién aplica...</p>}

          {plan && desglose && (
            <div className="rounded-md border border-border p-3 text-sm">
              <p>
                <strong>{ids.length}</strong> {etiquetaUnidad} → <strong>{desglose.total}</strong> jugador(es) inscrito(s)
              </p>
              {/* Los tres cubos, no el total suelto: el que importa es el
                  tercero, y antes ni se nombraba. */}
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                <li><strong className="text-foreground">{desglose.sinPlan.length}</strong> sin plan</li>
                <li>
                  <strong className="text-foreground">{desglose.conEste.length}</strong> ya lo tienen
                  {' '}(no se duplica: solo se completan las parcialidades que falten)
                </li>
                <li><strong className="text-foreground">{desglose.conOtro.length}</strong> tienen otro plan</li>
              </ul>
              {/* El monto cuenta a quien recibe el plan COMPLETO (sin plan +
                  con otro). A quien ya lo tiene solo se le completa lo que le
                  falte, que no se puede saber sin consultar sus cargos. */}
              <p className="mt-2 border-t border-border pt-2">
                Total a generar: <strong>{formatMoney(porJugador * nuevos)}</strong>
                {parcialidades > 0 && <> en {parcialidades} parcialidad(es)</>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatMoney(porJugador)} × {nuevos} jugador(es) que aún no lo tienen, antes de
                promociones y descuentos por hermanos. A quien ya lo tiene solo se le completan las
                parcialidades que le falten.
              </p>
            </div>
          )}

          {/* El caso destructivo se dice, no se insinua: reasignarPlanJugador
              cancela los cargos pendientes y vencidos de los otros planes que
              compartan concepto. No se bloquea la operacion (es legitima),
              pero el admin tiene que enterarse ANTES de confirmar. */}
          {plan && desglose && desglose.conOtro.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                A <strong>{desglose.conOtro.length}</strong> jugador(es) se les reemplazará su plan
                actual: se les cancelarán los cargos pendientes de ese plan y se generarán los de
                "{plan.nombre}". Lo ya pagado no se toca.
              </AlertDescription>
            </Alert>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => manejarOpenChange(false)} disabled={aplicando}>Cancelar</Button>
          <Button
            onClick={confirmar}
            disabled={!plan || !preview?.jugadorIds.length || aplicando}
          >
            {aplicando ? 'Asignando...' : `Asignar a ${preview?.jugadorIds.length || 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AsignarPlanDialog
